import {
  Box,
  MenuItem,
  TextField,
  Typography,
  Grid,
  Button,
  IconButton,
} from "@mui/material";
import RoundedTextField from "../../../components/RoundedProfileTextField";
import { updateFields } from "../../../redux/profileSlice";
import { useDispatch, useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";
import { Provinces } from "../../../constants/RegionalLocationList";
import RoundedSelect from "../../../components/RoundedSelect";
import { CheckCircle } from "@mui/icons-material";
import BiographyTips from "../../../components/BiographyTips";
import AgeGateDialog from "../../../features/onboarding/components/AgeGateDialog";
import {
  fromIsoDate,
  meetsMinimumAge,
  toIsoDate,
} from "../../../features/onboarding/age";
import { BIOGRAPHY_MAX_LENGTH } from "./biography";

// The API also accepts "future" and "other"; add them here if the editor
// supports those employment periods in the future.
const EMPLOYMENT_PERIOD_OPTIONS = [
  { value: "current", label: "Current" },
  { value: "past", label: "Past" },
];

const EditProfileRightSection = ({ localProfile, onDobValidityChange }) => {
  const dispatch = useDispatch();
  const profile = localProfile?.profile ?? {};
  const demographics = localProfile?.demographics ?? {};
  const [dobError, setDobError] = useState("");
  const [viewMore, setViewMore] = useState(false);
  const [openTips, setOpenTips] = useState(false);
  const [showAgeGate, setShowAgeGate] = useState(false);
  const updatedFields = useSelector(
    (state) => state.profileSlice?.updatedFields,
  );
  // Only an edited bio counts; a stored one over the limit loads intact.
  const isBiographyOverLimit =
    updatedFields?.biography?.length > BIOGRAPHY_MAX_LENGTH;

  const existingDob = demographics.dateOfBirth;
  const initialDob = fromIsoDate(existingDob?.slice?.(0, 10));
  const [dobMonth, setDobMonth] = useState(initialDob.month);
  const [dobDay, setDobDay] = useState(initialDob.day);
  const [dobYear, setDobYear] = useState(initialDob.year);

  const handleDobChange = (field, value) => {
    const cleaned = String(value)
      .replace(/\D/g, "")
      .slice(0, field === "year" ? 4 : 2);
    const next = {
      month: dobMonth,
      day: dobDay,
      year: dobYear,
      [field]: cleaned,
    };
    setDobMonth(next.month);
    setDobDay(next.day);
    setDobYear(next.year);
    setDobError("");

    const isoDate = toIsoDate(next);
    if (!isoDate) {
      setDobError("Enter a valid date of birth.");
      onDobValidityChange?.(true);
      return;
    }

    if (!meetsMinimumAge(isoDate)) {
      setShowAgeGate(true);
      setDobMonth(initialDob.month);
      setDobDay(initialDob.day);
      setDobYear(initialDob.year);
      onDobValidityChange?.(false);
      return;
    }

    onDobValidityChange?.(false);
    dispatch(
      updateFields({
        field: "demographics.dateOfBirth",
        value: `${isoDate}T00:00:00.000Z`,
      }),
    );
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateFields({ field: name, value }));
  };

  const handleAddFields = (name) => {
    if (name === "licenses") {
      const value = [
        // Optional: the role section is null for a member created through the
        // redesigned onboarding, which has no sub-profile rows (ECH-385). This
        // sits in a click handler rather than render, so the profile opens
        // fine and only adding a row crashes.
        ...(profile.licenses ?? []),
        { institute: "", license: "", year: "" },
      ];
      dispatch(
        updateFields({
          field: "licenses",
          value,
        }),
      );
      setViewMore(true);
    }
    if (name === "employment") {
      const value = [
        ...(profile.employmentHistory ?? []),
        { jobTitle: "", employer: "", employmentPeriod: "" },
      ];
      dispatch(
        updateFields({
          field: "employmentHistory",
          value,
        }),
      );
      setViewMore(true);
    }
  };

  const handleLicensesChange = (e, index) => {
    const { name } = e.target;
    const updatedLicenses = [
      // `?? []` matters: spreading an optional chain that resolves to
      // undefined still throws.
      ...(profile.licenses ?? []),
    ]; // Create a copy of the array
    if (name === "year") {
      let { value } = e.target;
      value = value.replace(/(?!^\+1\s)\D/g, "");
      value = value.slice(0, 4);
      updatedLicenses[index] = {
        ...updatedLicenses[index], // Preserve other fields in the object
        [name]: value,
      };
    } else {
      updatedLicenses[index] = {
        ...updatedLicenses[index], // Preserve other fields in the object
        [name]: e.target.value, // Update the degree field
      };
    }

    dispatch(
      updateFields({
        field: "licenses",
        value: updatedLicenses, // Update the entire array
      }),
    );
  };

  const handleEmploymentChange = (e, index) => {
    const { name } = e.target;
    let updatedEmployment = [...(profile.employmentHistory ?? [])];
    updatedEmployment[index] = {
      ...updatedEmployment[index], // Preserve other fields in the object
      [name]: e.target.value, // Update the degree field
    };
    updatedEmployment = updatedEmployment.map(
      ({ ...employment }) => employment,
    );
    dispatch(
      updateFields({
        field: "employmentHistory",
        value: updatedEmployment, // Update the entire array
      }),
    );
  };

  const handleDelete = (index, name) => {
    if (name === "licenses") {
      const value = (profile.licenses ?? []).filter((ed, i) => i !== index);
      dispatch(
        updateFields({
          field: "licenses",
          value,
        }),
      );
    } else {
      let value = (profile.employmentHistory ?? []).filter(
        (ed, i) => i !== index,
      );
      value = value.map(({ ...employment }) => employment);
      dispatch(
        updateFields({
          field: "employmentHistory",
          value,
        }),
      );
    }
  };

  const employmentInformation = Object.hasOwn(
    updatedFields,
    "employmentHistory",
  )
    ? updatedFields.employmentHistory
    : (profile.employmentHistory ?? []);

  const specializedLicencesOrRatings = Object.hasOwn(updatedFields, "licenses")
    ? updatedFields.licenses
    : (profile.licenses ?? []);
  const biography = updatedFields?.biography ?? profile.biography ?? "";
  return (
    <>
      <Box sx={{ width: "100%", marginBottom: "20px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              textAlign: "left",
              marginRight: "10px",
              fontWeight: 500,
            }}
          >
            Biography
          </Typography>
          <Button
            sx={{
              my: 1.5,
              padding: "6px 22px",
              borderRadius: "24px",
              textTransform: "none",
              color: "#fff",
              backgroundColor: "#7BB9D5",
              fontWeight: 600,
            }}
            onClick={() => setOpenTips(true)}
          >
            Tips
          </Button>
        </Box>
        <TextField
          multiline
          rows={4}
          placeholder="Write your biography here..."
          variant="outlined"
          fullWidth
          name="biography"
          value={biography}
          onChange={handleInputChange}
          inputProps={{ maxLength: BIOGRAPHY_MAX_LENGTH }}
          InputProps={{
            sx: {
              borderRadius: "12px",
              backgroundColor: "#F4F9FE",
              border: "1px solid #2A4876",
              padding: "10px",
              textTransform: "capitalize",
              "& .MuiInputBase-input": {
                color: "#2A4876",
              },
            },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            textAlign: "right",
            marginTop: "8px",
            color: isBiographyOverLimit ? "#B3312C" : "#7A7A7A",
          }}
        >
          {`${biography.length}/${BIOGRAPHY_MAX_LENGTH}`}
        </Typography>
      </Box>
      {/* Specialized Licenses Section */}
      <Box sx={{ width: "100%", marginBottom: "20px" }}>
        <Typography
          sx={{
            textAlign: "left",
            marginBottom: "8px",
            fontWeight: 500,
          }}
        >
          Credentials & Licences
        </Typography>
        {(viewMore
          ? specializedLicencesOrRatings
          : specializedLicencesOrRatings.slice(0, 3)
        )?.map((license, index) => (
          <Grid container spacing={1} key={license.id ?? index} mb={2}>
            <Grid item xs={3}>
              <RoundedTextField
                placeholder="License/Rating"
                name="license"
                value={license.license}
                onChange={(e) => handleLicensesChange(e, index)}
                fullWidth
                variant="outlined"
                sx={{
                  border: "1px solid #2A4876",
                  textTransform: "capitalize",
                  "& .MuiInputBase-input": {
                    color: "#2A4876",
                    fontSize: "13px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <RoundedTextField
                placeholder="Where was this obtained?"
                name="institute"
                value={license.institute}
                onChange={(e) => handleLicensesChange(e, index)}
                fullWidth
                variant="outlined"
                sx={{
                  border: "1px solid #2A4876",
                  textTransform: "capitalize",
                  "& .MuiInputBase-input": {
                    color: "#2A4876",
                    fontSize: "13px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <RoundedTextField
                  name="year"
                  value={license.year}
                  onChange={(e) => handleLicensesChange(e, index)}
                  fullWidth
                  placeholder="Year"
                  InputProps={{
                    disableUnderline: true, // Disable the underline to keep the style clean
                  }}
                  sx={{
                    textTransform: "capitalize",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: { xs: "20px", sm: "40px" },
                      backgroundColor: "#F1F7FA", // Set the input background color
                      height: "51px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2A4876", // Keep consistent border color
                    },
                    "& .MuiInputBase-input": {
                      color: "#2A4876",
                      borderRadius: { xs: "20px", sm: "50px" },
                      fontSize: "11px",
                      paddingLeft: "13px",
                      paddingRight: "13px",
                    },
                  }}
                />
                <IconButton
                  aria-label={`Delete credential ${index + 1}`}
                  onClick={() => handleDelete(index, "licenses")}
                  size="small"
                >
                  <DeleteIcon style={{ color: "#2A4876" }} />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        ))}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "90%",
          }}
        >
          <Box sx={{ textAlign: "left" }}>
            {profile.licenses?.length > 3 && (
              <Typography
                onClick={() => setViewMore((viewMore) => !viewMore)}
                sx={{ cursor: "pointer", fontWeight: "525" }}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setViewMore((value) => !value);
                  }
                }}
              >
                {viewMore ? "View Less" : "View More"}
              </Typography>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              cursor: "pointer",
              color: "#152134",
              //marginTop: '4px',
              alignItems: "right",
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleAddFields("licenses");
              }
            }}
            onClick={() => handleAddFields("licenses")}
          >
            Add More
          </Typography>
        </Box>
      </Box>
      <Box sx={{ width: "100%", marginBottom: "20px" }}>
        <Typography
          sx={{
            textAlign: "left",
            marginBottom: "8px",
            fontWeight: 500,
          }}
        >
          Employment Information
        </Typography>
        {(viewMore
          ? employmentInformation
          : employmentInformation.slice(0, 3)
        )?.map((employment, index) => (
          <Grid container spacing={1} key={index} mb={2}>
            <Grid item xs={4}>
              <RoundedTextField
                placeholder="JobTitle"
                name="jobTitle"
                value={employment.jobTitle}
                onChange={(e) => handleEmploymentChange(e, index)}
                fullWidth
                variant="outlined"
                sx={{
                  border: "1px solid #2A4876",
                  textTransform: "capitalize",
                  "& .MuiInputBase-input": {
                    color: "#2A4876",
                    fontSize: "13px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <RoundedTextField
                  placeholder="Employer"
                  name="employer"
                  value={employment.employer}
                  onChange={(e) => handleEmploymentChange(e, index)}
                  variant="outlined"
                  sx={{
                    border: "1px solid #2A4876",
                    textTransform: "capitalize",
                    "& .MuiInputBase-input": {
                      color: "#2A4876",
                      fontSize: "13px",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <RoundedSelect
                  value={employment?.employmentPeriod || ""}
                  name="employmentPeriod"
                  onChange={(e) => handleEmploymentChange(e, index)}
                  displayEmpty
                  renderValue={(selected) => {
                    const label = EMPLOYMENT_PERIOD_OPTIONS.find(
                      ({ value }) => value === selected,
                    )?.label;
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          justifyContent: "space-between",
                          color: selected ? "#2A4876" : "#AAB6C8",
                        }}
                      >
                        <span>{label || selected || "Select"}</span>
                      </Box>
                    );
                  }}
                  inputProps={{ "aria-label": "Employment period" }}
                  sx={{
                    width: "80%",
                    "& .MuiOutlinedInput-root": {
                      background: "#F1F7FA",
                    },
                    fontSize: "13px",
                    border: "1px solid #2A4876",
                  }} // Remove default label
                >
                  {EMPLOYMENT_PERIOD_OPTIONS.map(({ value, label }) => (
                    <MenuItem
                      sx={{
                        color: "#2A4876",
                      }}
                      key={value}
                      value={value}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        {label}{" "}
                        {employment?.employmentPeriod === value && (
                          <CheckCircle
                            sx={{
                              marginLeft: "3px",
                              color: "#2A4876",
                              width: "14px",
                            }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </RoundedSelect>
                <IconButton
                  aria-label={`Delete employment ${index + 1}`}
                  onClick={() => handleDelete(index, "employment")}
                  size="small"
                >
                  <DeleteIcon style={{ color: "#2A4876" }} />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        ))}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "90%",
          }}
        >
          <Box sx={{ textAlign: "left" }}>
            {profile.employmentHistory?.length > 3 && (
              <Typography
                onClick={() => setViewMore((viewMore) => !viewMore)}
                sx={{ cursor: "pointer", fontWeight: "525" }}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setViewMore((value) => !value);
                  }
                }}
              >
                {viewMore ? "View Less" : "View More"}
              </Typography>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              cursor: "pointer",
              color: "#152134",
              //marginTop: '4px',
              alignItems: "right",
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleAddFields("employment");
              }
            }}
            onClick={() => handleAddFields("employment")}
          >
            Add More
          </Typography>
        </Box>
      </Box>
      {/* Date of Birth */}
      <Typography
        sx={{ textAlign: "left", marginTop: "20px", marginBottom: "5px" }}
        component="dt"
      >
        Date of Birth
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "35%",
              md: "45%",
            },
          }}
        >
          <TextField
            value={dobDay}
            onChange={(event) => handleDobChange("day", event.target.value)}
            placeholder="Day"
            inputProps={{
              "aria-label": "Birth day",
              inputMode: "numeric",
              maxLength: 2,
            }}
            fullWidth
            size="small"
            sx={{ border: "1px solid #2A4876" }}
          />
        </Box>

        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "30%",
              md: "50%",
              lg: "24%",
            },
          }}
        >
          <TextField
            value={dobMonth}
            onChange={(event) => handleDobChange("month", event.target.value)}
            placeholder="Month"
            inputProps={{
              "aria-label": "Birth month",
              inputMode: "numeric",
              maxLength: 2,
            }}
            fullWidth
            size="small"
            sx={{ border: "1px solid #2A4876" }}
          />
        </Box>

        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "30%",
              md: "50%",
              lg: "25%",
            },
          }}
        >
          <TextField
            value={dobYear}
            onChange={(event) => handleDobChange("year", event.target.value)}
            placeholder="Year"
            inputProps={{
              "aria-label": "Birth year",
              inputMode: "numeric",
              maxLength: 4,
            }}
            fullWidth
            size="small"
            sx={{ border: "1px solid #2A4876" }}
          />
        </Box>
      </Box>
      {dobError && (
        <Typography role="alert" sx={{ color: "#B3312C", mt: 1 }}>
          {dobError}
        </Typography>
      )}
      <Typography
        sx={{ textAlign: "left", marginTop: "20px", marginBottom: "5px" }}
        component="dt"
      >
        Your City
      </Typography>
      <RoundedTextField
        fullWidth
        size="small"
        value={updatedFields?.address?.city ?? profile.address?.city ?? ""}
        name="address.city"
        inputProps={{ "aria-label": "City" }}
        onChange={handleInputChange}
        sx={{
          borderRadius: "50px",
          backgroundColor: "#F1F7FA",
          border: "1px solid #2A4876",
          textTransform: "capitalize",
          "& .MuiInputBase-input": {
            color: "#2A4876",
          },
        }}
      />
      <Typography
        sx={{ textAlign: "left", marginTop: "20px", marginBottom: "5px" }}
        component="dt"
      >
        Your Province
      </Typography>
      <RoundedSelect
        sx={{
          // Set the width directly for the select component
          "& .MuiInputBase-input": {
            padding: "15px",
            color: "#2A4876",
          },
          textAlign: "left",
        }}
        fullWidth
        name="address.province"
        value={
          updatedFields?.address?.province ?? profile.address?.province ?? ""
        }
        displayEmpty
        onChange={handleInputChange}
        renderValue={(selected) => {
          // Show placeholder when no value is selected
          if (!selected) {
            return <span style={{ color: "#AAB6C8" }}>Select</span>;
          }
          return selected;
        }}
        inputProps={{ "aria-label": "Province" }}
      >
        {Provinces.map((province) => (
          <MenuItem sx={{ color: "#2A4876" }} key={province} value={province}>
            {province}
            {province === profile.address?.province && (
              <CheckCircle
                sx={{
                  marginLeft: "auto",
                  color: "#2A4876",
                  width: "14px",
                }}
              />
            )}{" "}
            {/* Display the text inside the MenuItem */}
          </MenuItem>
        ))}
      </RoundedSelect>
      <Typography
        sx={{ textAlign: "left", marginTop: "20px", marginBottom: "5px" }}
        component="dt"
      >
        Postal Code
      </Typography>
      <RoundedTextField
        fullWidth
        size="small"
        value={
          updatedFields?.address?.postalCode ??
          profile.address?.postalCode ??
          ""
        }
        name="address.postalCode"
        inputProps={{ "aria-label": "Postal code" }}
        onChange={handleInputChange}
        sx={{
          borderRadius: "50px",
          backgroundColor: "#F1F7FA",
          border: "1px solid #2A4876",
          "& .MuiInputBase-input": { color: "#2A4876" },
        }}
      />
      <Typography
        sx={{ textAlign: "left", marginTop: "20px", marginBottom: "5px" }}
        component="dt"
      >
        Country
      </Typography>
      <RoundedTextField
        fullWidth
        size="small"
        value={
          updatedFields?.address?.country ?? profile.address?.country ?? ""
        }
        name="address.country"
        inputProps={{ "aria-label": "Country" }}
        onChange={handleInputChange}
        sx={{
          borderRadius: "50px",
          backgroundColor: "#F1F7FA",
          border: "1px solid #2A4876",
          "& .MuiInputBase-input": { color: "#2A4876" },
        }}
      />

      {openTips && (
        <BiographyTips open={openTips} onClose={() => setOpenTips(false)} />
      )}
      {showAgeGate && (
        <AgeGateDialog
          open={showAgeGate}
          onClose={() => setShowAgeGate(false)}
        />
      )}
    </>
  );
};

export default EditProfileRightSection;
